import { useEffect, useRef, useState, type ReactNode } from "react"

export function useBulkSelection(availableIds: readonly string[]) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const availableKey = availableIds.join("\u0000")

  useEffect(() => {
    const available = new Set(availableIds)
    setSelectedIds((current) => {
      const next = new Set([...current].filter((id) => available.has(id)))
      return next.size === current.size ? current : next
    })
  }, [availableKey])

  function toggle(id: string) {
    setSelectedIds((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleAll(ids: readonly string[]) {
    setSelectedIds((current) => {
      const next = new Set(current)
      const allSelected = ids.length > 0 && ids.every((id) => next.has(id))
      for (const id of ids) {
        if (allSelected) next.delete(id)
        else next.add(id)
      }
      return next
    })
  }

  function remove(id: string) {
    setSelectedIds((current) => {
      if (!current.has(id)) return current
      const next = new Set(current)
      next.delete(id)
      return next
    })
  }

  return {
    selectedIds,
    selectedCount: selectedIds.size,
    isSelected: (id: string) => selectedIds.has(id),
    isAllSelected: (ids: readonly string[]) => ids.length > 0 && ids.every((id) => selectedIds.has(id)),
    isSomeSelected: (ids: readonly string[]) => ids.some((id) => selectedIds.has(id)) && !ids.every((id) => selectedIds.has(id)),
    toggle,
    toggleAll,
    remove,
    clear: () => setSelectedIds(new Set()),
  }
}

export function BulkSelectCheckbox({
  checked,
  indeterminate = false,
  onChange,
  label,
  disabled = false,
}: {
  checked: boolean
  indeterminate?: boolean
  onChange: () => void
  label: string
  disabled?: boolean
}) {
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = indeterminate
  }, [indeterminate])

  return (
    <input
      ref={inputRef}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      aria-label={label}
      className="h-4 w-4 rounded border-gold-500 text-forest-950 accent-forest-950 focus:ring-2 focus:ring-gold-500"
    />
  )
}

export function BulkActionBar({
  selectedCount,
  onClear,
  children,
}: {
  selectedCount: number
  onClear: () => void
  children: ReactNode
}) {
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-forest-700/20 bg-forest-950 px-4 py-3 text-white sm:flex-row sm:items-center sm:justify-between sm:px-5">
      <p className="text-sm font-medium" aria-live="polite">
        {selectedCount} selected
      </p>
      <div className="flex flex-wrap items-center gap-2">
        {children}
        <button
          type="button"
          onClick={onClear}
          className="rounded-full border border-white/35 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-white/10"
        >
          Clear selection
        </button>
      </div>
    </div>
  )
}
